import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {getRatingPropertyPaths} from './PropertyPaths';
import {A360Plan, A360ProductCd} from 'Quoting/models/Quoting';
import {getFieldError, getValidationRuleset} from '../rules/rulesValidator';
import {VALUE_NOT_AVAILABLE, isDefined} from 'Quoting/utils/utilities';
import {fetchHeapedValues, getRate} from 'Quoting/components/QuoteForm/AgenciesCompensation/CompensationTypeForm';
import {AvailablePlans} from '../types';
import {setCompensationValue} from 'Quoting/ducks/compensation';

export const RatingBlock = () => {
  const {selectedPlan, selectedProduct, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {compensationValue} = useSelector((state: ReducerState) => state.compensation);
  const {decisionTableLevelResponse, decisionTableHeapedResponse} = useSelector(
    (state: ReducerState) => state.quoteActions
  );

  const dispatch = useDispatch();

  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const productCd = selectedProduct?.code ?? '';
  const propertiesPath = getRatingPropertyPaths(selectedPlan?.planIndex);

  const rateFormatErrorKey = `${productCd}CoverageRating.rateFormatCd`;
  const rateStructureErrorKey = `${productCd}CoverageRating.rateStructureCd`;

  const ratingError = (error: string, path: string) =>
    getFieldError(error, getQuoteFieldValue<string>(path), getValidationRuleset(selectedProduct?.code));

  if (selectedProduct?.code !== A360ProductCd.CI) {
    const ciRateStructureError = `${A360ProductCd.CI}CoverageRating.rateStructureCd`
    if (isDefined(quoteFormErrors[ciRateStructureError])) {
      delete quoteFormErrors[ciRateStructureError];
    }
  }
  const hasRateStructureError =
    isDefined(ratingError(rateStructureErrorKey, 'rateStructureCd')) && !getQuoteFieldValue('rateStructureCd');
  quoteFormErrors[rateStructureErrorKey] = hasRateStructureError ? 
    ratingError(rateStructureErrorKey, 'rateStructureCd') : undefined;

  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;

  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';

  const updateHeapedValue = (issueAge: String) => {
    if (
      selectedProduct?.code === A360ProductCd.CI &&
      compensationValue?.type !== '' &&
      decisionTableLevelResponse?.length > 0
    ) {
      const heapedValue = fetchHeapedValues(
        decisionTableHeapedResponse,
        selectedPlanCd as AvailablePlans,
        situsStateCd,
        issueAge === 'IssueAge',
        isDefined(decisionTableLevelResponse[0]) ? decisionTableLevelResponse[0]?.maxCommissionLevel?.toString() : '0'
      );

      dispatch(
        setCompensationValue({
          ...compensationValue,
          rate:
            compensationValue?.type === 'Heaped'
              ? (selectedPlanCd === A360Plan.GCI5 || selectedPlanCd === A360Plan.GVCI4) && heapedValue?.length > 1
                ? heapedValue.find(obj => obj.heapedIssueAge === (issueAge === 'IssueAge'))
                    ?.code
                : heapedValue[heapedValue.length - 1]?.code
              : selectedPlanCd === A360Plan.GVCI4
              ? getRate(
                  selectedPlanCd,
                  situsStateCd,
                  issueAge === 'IssueAge',
                  decisionTableLevelResponse[0]?.maxCommissionLevel?.toString()
                )
              : Math.min(Number(decisionTableLevelResponse[0]?.maxCommissionLevel), 20).toString(),
        })
      );
    }
  };

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'rate-format-row-1'},
      items: [
        {
          key: propertiesPath.rateBasisCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}CoverageRating.rateBasisCd`}
              label="Rate Basis"
              id={propertiesPath.rateBasisCd}
              value={getQuoteFieldValue(propertiesPath.rateBasisCd)}
              onChange={e => updateQuoteFieldValue(propertiesPath.rateBasisCd, e.target.value as string)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertiesPath.rateFormatCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}CoverageRating.rateFormatCd`}
              label="Rate Format"
              id={propertiesPath.rateFormatCd}
              value={getQuoteFieldValue(propertiesPath.rateFormatCd)}
              onChange={e => updateQuoteFieldValue(propertiesPath.rateFormatCd, e.target.value as string)}
              error={isDefined(ratingError(rateFormatErrorKey, propertiesPath.rateFormatCd))}
              helperText={ratingError(rateFormatErrorKey, propertiesPath.rateFormatCd)}
              disabled
              required={productCd !== A360ProductCd.HI}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: `rateStructureCd-${selectedPlan?.planIndex}`,
          hidden: selectedProduct?.code !== A360ProductCd.CI,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}MasterPolicySummary.rateStructureCd`}
              label="Rate Structure"
              id={`rateStructureCd-${selectedPlan?.planIndex}`}
              value={getQuoteFieldValue('rateStructureCd')}
              onChange={e => {
                updateQuoteFieldValue('rateStructureCd', e.target.value as string);
                updateHeapedValue(e.target.value as string);
              }}
              error={hasRateStructureError}
              helperText={hasRateStructureError ? ratingError(rateStructureErrorKey, 'rateStructureCd') : ''}
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Rating" collapsible withSeparator expanded={selectedProduct?.code === A360ProductCd.CI}>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
